import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Loader2, AlertTriangle } from 'lucide-react'
import { SimpleClient } from '../../model/Client'
import { Event } from '../../model/Event'
import { SimpleIdentity } from '../../model/Identity'
import IdentityEndpoints from '../../endpoints/IdentityEndpoints'
import { toast } from 'sonner'

const NOT_PROVISIONED = -1
const DEFAULT_ACCREDITATION_ID = -1

interface CreateIdentityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clients: SimpleClient[]
  onCreated: (identityId: number) => void
  provisioningEvent?: Event | null
}

export const CreateIdentityDialog: React.FC<CreateIdentityDialogProps> = ({
  open,
  onOpenChange,
  clients,
  onCreated,
  provisioningEvent,
}) => {
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    clientId: '',
    firstName: '',
    lastName: '',
    email: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [similarIdentities, setSimilarIdentities] = useState<SimpleIdentity[]>(
    []
  )
  const [roleId, setRoleId] = useState<number>(NOT_PROVISIONED)
  const [accreditationLevelId, setAccreditationLevelId] = useState<number>(
    DEFAULT_ACCREDITATION_ID
  )
  const [secondaryRoleName, setSecondaryRoleName] = useState('')

  // When provisioning, the event's own client has to be selectable even if it's
  // archived or hasn't made it into the application store yet — otherwise the
  // locked-in selection renders as an empty placeholder.
  const activeClients = useMemo(() => {
    const active = clients.filter((c) => !c.dateArchived)
    const eventClient = provisioningEvent?.client
    if (eventClient && !active.some((c) => c.id === eventClient.id)) {
      return [eventClient, ...active]
    }
    return active
  }, [clients, provisioningEvent])

  const resetForm = () => {
    // Keep the event's client when provisioning: the selector is disabled in that
    // mode, so clearing it would leave the form permanently unsubmittable on the
    // second open.
    setFormData({
      clientId: provisioningEvent ? provisioningEvent.client.id.toString() : '',
      firstName: '',
      lastName: '',
      email: '',
    })
    setErrors({})
    setSimilarIdentities([])
    setRoleId(NOT_PROVISIONED)
    setAccreditationLevelId(DEFAULT_ACCREDITATION_ID)
    setSecondaryRoleName('')
  }

  // Pre-lock client when a provisioning event is supplied.
  useEffect(() => {
    if (provisioningEvent) {
      setFormData((p) => ({
        ...p,
        clientId: provisioningEvent.client.id.toString(),
      }))
    }
  }, [provisioningEvent])

  // Duplicate detection: debounced search keyed off both names.
  useEffect(() => {
    const fn = formData.firstName.trim()
    const ln = formData.lastName.trim()
    if (!fn || !ln) {
      setSimilarIdentities([])
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      IdentityEndpoints.getIdentityList(undefined, fn, ln)
        .then((ids) => {
          if (cancelled) return
          setSimilarIdentities(ids)
        })
        .catch((err) => {
          console.error('Error while searching for duplicates', err)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [formData.firstName, formData.lastName])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.clientId) newErrors.clientId = 'Client is required'
    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required'
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setSaving(true)
    try {
      const identity = await IdentityEndpoints.createIdentity({
        clientId: parseInt(formData.clientId),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim() || undefined,
      })
      toast.success(`${identity.firstName} ${identity.lastName} created`)

      if (provisioningEvent && roleId !== NOT_PROVISIONED) {
        try {
          await IdentityEndpoints.provisionForEvent(
            identity.id,
            provisioningEvent.id,
            {
              roleId,
              accreditationLevelId:
                accreditationLevelId === DEFAULT_ACCREDITATION_ID
                  ? null
                  : accreditationLevelId,
              secondaryRoleName: secondaryRoleName.trim(),
            }
          )
          toast.success(`Provisioned to ${provisioningEvent.name}`)
        } catch (err: any) {
          toast.error(
            `Identity created, but provisioning to ${provisioningEvent.name} failed`
          )
        }
      }

      resetForm()
      onOpenChange(false)
      onCreated(identity.id)
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create identity'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const schedulableRoles = provisioningEvent
    ? provisioningEvent.roles
        .filter((r) => r.schedulable)
        .sort((a, b) => a.internalName.localeCompare(b.internalName))
    : []
  const nonSchedulableRoles = provisioningEvent
    ? provisioningEvent.roles
        .filter((r) => !r.schedulable)
        .sort((a, b) => a.internalName.localeCompare(b.internalName))
    : []
  const sortedAccLevels = provisioningEvent
    ? [...provisioningEvent.accreditationLevels].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    : []

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetForm()
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Identity</DialogTitle>
          <DialogDescription>
            {provisioningEvent
              ? `Add a new identity and provision them to ${provisioningEvent.name}.`
              : 'Add a new identity to the system.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Client *</Label>
            <div className="col-span-3">
              <Select
                value={formData.clientId}
                onValueChange={(v) => {
                  setFormData((p) => ({ ...p, clientId: v }))
                  if (errors.clientId) setErrors((p) => ({ ...p, clientId: '' }))
                }}
                disabled={!!provisioningEvent || saving}
              >
                <SelectTrigger
                  className={errors.clientId ? 'border-red-500' : ''}
                >
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {activeClients.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.clientId && (
                <p className="text-sm text-red-500 mt-1">{errors.clientId}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="firstName" className="text-right">
              First Name *
            </Label>
            <div className="col-span-3">
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, firstName: e.target.value }))
                  if (errors.firstName)
                    setErrors((p) => ({ ...p, firstName: '' }))
                }}
                className={errors.firstName ? 'border-red-500' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-red-500 mt-1">{errors.firstName}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="lastName" className="text-right">
              Last Name *
            </Label>
            <div className="col-span-3">
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => {
                  setFormData((p) => ({ ...p, lastName: e.target.value }))
                  if (errors.lastName)
                    setErrors((p) => ({ ...p, lastName: '' }))
                }}
                className={errors.lastName ? 'border-red-500' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-red-500 mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">
              Email
            </Label>
            <div className="col-span-3">
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="Optional"
              />
            </div>
          </div>

          {provisioningEvent && (
            <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
              <div className="text-sm font-medium">
                Initial Provisioning for {provisioningEvent.name}
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Role</Label>
                <div className="col-span-3">
                  <Select
                    value={roleId.toString()}
                    onValueChange={(v) => setRoleId(parseInt(v))}
                    disabled={saving}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NOT_PROVISIONED.toString()}>
                        Not Provisioned
                      </SelectItem>
                      {schedulableRoles.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>Schedulable</SelectLabel>
                          {schedulableRoles.map((r) => (
                            <SelectItem key={r.id} value={r.id.toString()}>
                              {r.internalName}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                      {nonSchedulableRoles.length > 0 && (
                        <SelectGroup>
                          <SelectLabel>Non-Schedulable</SelectLabel>
                          {nonSchedulableRoles.map((r) => (
                            <SelectItem key={r.id} value={r.id.toString()}>
                              {r.internalName}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Accreditation</Label>
                <div className="col-span-3">
                  <Select
                    value={accreditationLevelId.toString()}
                    onValueChange={(v) =>
                      setAccreditationLevelId(parseInt(v))
                    }
                    disabled={saving || roleId === NOT_PROVISIONED}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT_ACCREDITATION_ID.toString()}>
                        Role Default
                      </SelectItem>
                      {sortedAccLevels.map((a) => (
                        <SelectItem key={a.id} value={a.id.toString()}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="secondaryRoleName" className="text-right">
                  Secondary Role
                </Label>
                <div className="col-span-3">
                  <Input
                    id="secondaryRoleName"
                    value={secondaryRoleName}
                    onChange={(e) => setSecondaryRoleName(e.target.value)}
                    disabled={saving || roleId === NOT_PROVISIONED}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
          )}

          {similarIdentities.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-900">
                  Similarly-named identities already exist. Please double-check
                  before creating a duplicate.
                </p>
              </div>
              <ul className="text-sm space-y-1 pl-6">
                {similarIdentities.map((i) => (
                  <li key={i.id}>
                    <a
                      href={`/identity/${i.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-900 underline hover:text-amber-700"
                    >
                      {i.firstName} {i.lastName}
                      {i.parentCustomer
                        ? ` — ${i.parentCustomer.name}`
                        : ' — Global'}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm()
              onOpenChange(false)
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Identity'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
